import React, { useEffect, useRef, useState } from "react";
import { api, arabicError, fileToBase64 } from "../lib/api";
import { toast } from "sonner";
import { ImagePlus, RefreshCw, Trash2, BadgeCheck, Stamp, PenLine } from "lucide-react";

const KINDS = [
  { key: "logo", label: "شعار المحل", hint: "يظهر أعلى الفاتورة بجانب العنوان", Icon: BadgeCheck },
  { key: "stamp", label: "ختم المحل", hint: "يظهر أسفل الفاتورة في خانة «ختم البائع»", Icon: Stamp },
  { key: "signature", label: "التوقيع", hint: "يظهر أسفل الفاتورة في خانة «توقيع البائع»", Icon: PenLine },
];
const ACCEPT = ["image/png", "image/jpeg", "image/webp"];

const IdentityCard = ({ kind, value, onChanged }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!ACCEPT.includes(f.type)) { toast.error("يُسمح بصور PNG أو JPG أو WEBP فقط"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("حجم الصورة كبير (الحد 2MB)"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/branding/mine/${kind.key}`, { image_base64: await fileToBase64(f) });
      toast.success(r.data.message);
      onChanged(kind.key, r.data[kind.key]);
    } catch (err) { toast.error(arabicError(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`حذف ${kind.label}؟`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/branding/mine/${kind.key}`);
      toast.success(r.data.message);
      onChanged(kind.key, null);
    } catch (err) { toast.error(arabicError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#7AB2B2] rounded-2xl p-4 flex flex-col gap-3" data-testid={`identity-card-${kind.key}`}>
      <div className="flex items-center gap-2">
        <kind.Icon size={18} className="text-[#09637E]" />
        <div className="font-extrabold text-[#333333]">{kind.label}</div>
        <span className="text-[11px] text-[#666666] mr-auto">اختياري</span>
      </div>
      <div className="h-32 rounded-xl bg-[#EBF4F6] border border-dashed border-[#7AB2B2] flex items-center justify-center overflow-hidden">
        {value
          ? <img src={value} alt={kind.label} className="max-h-full max-w-full object-contain p-2" data-testid={`identity-preview-${kind.key}`} />
          : <span className="text-xs text-[#666666]" data-testid={`identity-empty-${kind.key}`}>لم تُرفع بعد</span>}
      </div>
      <p className="text-xs text-[#666666]">{kind.hint}</p>
      <input ref={ref} type="file" accept={ACCEPT.join(",")} className="hidden" onChange={onFile} data-testid={`identity-input-${kind.key}`} />
      <div className="flex gap-2">
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`identity-upload-${kind.key}`}
          className="flex-1 h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {value ? <RefreshCw size={16} /> : <ImagePlus size={16} />} {busy ? "جارٍ الحفظ..." : value ? "استبدال" : "رفع صورة"}
        </button>
        {value && (
          <button onClick={remove} disabled={busy} data-testid={`identity-delete-${kind.key}`}
            className="h-11 w-11 rounded-lg border-2 border-red-400 text-red-500 flex items-center justify-center hover:bg-red-50 disabled:opacity-60">
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

const InvoiceIdentity = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/branding/mine").then((r) => setData(r.data)).catch((e) => { toast.error(arabicError(e)); setData({}); });
  }, []);
  if (!data) return <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div>;
  return (
    <div className="space-y-3" data-testid="invoice-identity">
      <p className="text-sm text-[#666666]">
        ارفع شعار محلك وختمك وتوقيعك مرة واحدة، وستظهر تلقائياً على كل فاتورة تصدرها كبائع عبر إمداد. جميعها اختيارية؛ الحد 2MB لكل صورة (PNG/JPG/WEBP).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {KINDS.map((k) => (
          <IdentityCard key={k.key} kind={k} value={data[k.key]} onChanged={(key, v) => setData((s) => ({ ...s, [key]: v }))} />
        ))}
      </div>
    </div>
  );
};

export default InvoiceIdentity;
